import { getStepPresentation } from './constants'
import type { TraceStep } from './types'

export function StepNode({
  step,
  index,
  active,
  visible,
  onClick,
}: {
  step: TraceStep
  index: number
  active: boolean
  visible: boolean
  onClick: () => void
}) {
  const presentation = getStepPresentation(step)
  const config = presentation.config
  const isTool = step.type === 'TOOL_CALL' || step.type === 'CACHE'
  const isLLM = step.type === 'LLM'
  const relatedFiles = presentation.relatedFiles
  const tools = presentation.tools
  const llmAnalysis = presentation.llmAnalysis

  const metaChips: Array<{ label: string; color: string; background: string }> = []

  if (step.type === 'LANGGRAPH_NODE_ENTER' || step.type === 'LANGGRAPH_NODE_EXIT') {
    if (typeof step.data.nodeName === 'string') {
      metaChips.push({ label: step.data.nodeName, color: '#e9d5ff', background: 'rgba(168,85,247,0.14)' })
    }
    if (typeof step.data.stageBefore === 'string') {
      metaChips.push({ label: `before: ${step.data.stageBefore}`, color: '#c7d2fe', background: 'rgba(99,102,241,0.12)' })
    }
    if (typeof step.data.stageAfter === 'string') {
      metaChips.push({ label: `after: ${step.data.stageAfter}`, color: '#bbf7d0', background: 'rgba(34,197,94,0.12)' })
    }
    if (step.data.failed) {
      metaChips.push({ label: 'FAILED', color: '#fecaca', background: 'rgba(220,38,38,0.18)' })
    }
  }

  if (step.type === 'RUNTIME_ROUTING_DECISION' || step.type === 'RUNTIME_SWITCH') {
    if (typeof step.data.runtime === 'string') {
      metaChips.push({ label: step.data.runtime, color: '#bae6fd', background: 'rgba(14,165,233,0.14)' })
    }
    if (typeof step.data.direction === 'string') {
      metaChips.push({ label: step.data.direction, color: '#cffafe', background: 'rgba(6,182,212,0.12)' })
    }
  }

  if (step.type === 'INITIAL_GRAPH_ROUTE' || step.type === 'FINAL_EFFECTIVE_ROUTE') {
    const route = step.data.finalRoute || step.data.initialRoute || step.data.route
    if (typeof route === 'string') {
      metaChips.push({ label: route, color: '#dcfce7', background: 'rgba(34,197,94,0.14)' })
    }
    if (step.data.escalated) {
      metaChips.push({ label: 'ESCALATED', color: '#fecaca', background: 'rgba(239,68,68,0.18)' })
    }
  }

  if (step.type === 'GRAPH_CHECKPOINT_LOAD' || step.type === 'GRAPH_CHECKPOINT_SAVE' || step.type === 'GRAPH_STATE_RESTORED') {
    if (step.data.checkpointId) {
      metaChips.push({ label: String(step.data.checkpointId).slice(0, 8), color: '#dbeafe', background: 'rgba(59,130,246,0.12)' })
    }
    if (step.data.restored || step.data.saved) {
      metaChips.push({ label: step.data.restored ? 'restored' : 'saved', color: '#dcfce7', background: 'rgba(34,197,94,0.12)' })
    }
  }

  if (step.type === 'TOOL_EXECUTION') {
    if (typeof step.data.toolName === 'string') {
      metaChips.push({ label: step.data.toolName, color: '#99f6e4', background: 'rgba(20,184,166,0.14)' })
    }
    if (step.data.cacheHit) {
      metaChips.push({ label: 'CACHE HIT', color: '#fcd34d', background: 'rgba(245,158,11,0.16)' })
    }
  }

  if (
    step.type === 'ESCALATION_REQUESTED' ||
    step.type === 'ESCALATION_PROPAGATED' ||
    step.type === 'ESCALATION_HANDOFF_TRIGGERED' ||
    step.type === 'SUPERVISOR_REDIRECT'
  ) {
    if (typeof step.data.reason === 'string') {
      metaChips.push({ label: step.data.reason.slice(0, 28), color: '#fecdd3', background: 'rgba(244,63,94,0.12)' })
    }
    if (typeof step.data.targetNode === 'string') {
      metaChips.push({ label: step.data.targetNode, color: '#fecdd3', background: 'rgba(244,63,94,0.12)' })
    }
  }

  if (step.type === 'SUPERVISOR_OVERRIDE') {
    const action = String(step.data.action ?? '').trim()
    const reason = String(step.data.reason ?? '').trim()
    let category = action
    if (action === 'support_objection' || reason === 'strong_objection') {
      category = 'support_objection'
    } else if (action === 'acknowledge_rejection' || reason === 'real_rejection') {
      category = 'acknowledge_rejection'
    }

    if (category) {
      metaChips.push({ label: category, color: '#fbcfe8', background: 'rgba(244,114,182,0.14)' })
    }

    if (typeof step.data.objectionSeverity === 'string' && step.data.objectionSeverity) {
      metaChips.push({ label: step.data.objectionSeverity, color: '#f9a8d4', background: 'rgba(244,114,182,0.10)' })
    }
  }

  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 8,
        opacity: visible ? 1 : 0.2,
        transform: visible ? 'translateX(0)' : 'translateX(-10px)',
        transition: `opacity .25s ease ${index * 0.03}s, transform .25s ease ${index * 0.03}s`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: active ? config.color : 'rgba(255,255,255,0.06)',
            border: `2px solid ${active ? config.color : 'rgba(255,255,255,0.12)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {config.icon}
        </div>
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', marginTop: 2 }} />
      </div>

      <div
        style={{
          flex: 1,
          background: active ? `linear-gradient(135deg, ${config.color}18, ${config.color}08)` : 'rgba(255,255,255,0.03)',
          border: `1px solid ${active ? `${config.color}44` : 'rgba(255,255,255,0.07)'}`,
          borderRadius: 10,
          padding: '10px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: config.color, textTransform: 'uppercase' }}>
              {step.type}
            </span>
            {isTool && (
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 7px',
                  borderRadius: 4,
                  background: step.data.cacheHit ? '#f59e0b22' : '#10b98122',
                  color: step.data.cacheHit ? '#f59e0b' : '#10b981',
                  fontWeight: 600,
                }}
              >
                {step.data.cacheHit ? 'CACHE HIT' : 'EXEC'}
              </span>
            )}
            {step.type === 'DECISION' && step.data.mode && (
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 7px',
                  borderRadius: 4,
                  background: step.data.mode === 'agent' ? '#6366f122' : '#94a3b822',
                  color: step.data.mode === 'agent' ? '#a78bfa' : '#94a3b8',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                }}
              >
                {step.data.mode === 'agent' ? 'AGENT MODE' : 'LEGACY'}
              </span>
            )}
            {tools.map((tool, idx) => (
              <span key={`${step.type}-${tool}-${idx}`} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(99,102,241,0.16)', color: '#c4b5fd', fontFamily: 'monospace', fontWeight: 600 }}>
                {tool}
              </span>
            ))}
          </div>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>+{step.ms}ms</span>
        </div>

        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>{presentation.summary}</div>

        {metaChips.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {metaChips.map((chip) => (
              <span
                key={`${step.type}-${chip.label}`}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: chip.background,
                  color: chip.color,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}

        {step.type === 'DECISION' && (
          <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
            strategy: <span style={{ color: '#06b6d4' }}>{step.data.strategy}</span>
            {' · '}
            {step.data.reason}
            {step.data.mode && <span style={{ color: step.data.mode === 'agent' ? '#a78bfa' : '#94a3b8' }}>{' · '}{step.data.mode}</span>}
          </div>
        )}

        {isTool && <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>params: {JSON.stringify(step.data.params || {})}</div>}

        {isTool && step.data.result !== undefined && (
          <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.42)', background: 'rgba(16,185,129,0.05)', borderLeft: '2px solid #10b981', padding: '4px 8px', borderRadius: '0 4px 4px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            result: {JSON.stringify(step.data.result)}
          </div>
        )}

        {step.type === 'COMPLEXITY' && Array.isArray(step.data.suggestedTools) && (
          <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {step.data.suggestedTools.map((tool: string, idx: number) => (
              <span key={`${tool}-${idx}`} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#6366f122', color: '#a78bfa' }}>
                {tool}
              </span>
            ))}
          </div>
        )}

        {isLLM && (
          <div style={{ marginTop: 6 }}>
            <div style={{ display: 'flex', gap: 12, fontSize: 10, fontFamily: 'monospace', flexWrap: 'wrap' }}>
              <span style={{ color: '#a78bfa' }}>↑ {step.data.inputTokens || 0}t</span>
              <span style={{ color: '#ec4899' }}>↓ {step.data.outputTokens || 0}t</span>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>{step.data.latencyMs || 0}ms</span>
            </div>
            {tools.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {tools.map((tool, idx) => (
                  <span key={`llm-tool-${tool}-${idx}`} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(236,72,153,0.12)', color: '#f9a8d4', fontFamily: 'monospace' }}>
                    tool: {tool}
                  </span>
                ))}
              </div>
            )}
            {llmAnalysis.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {llmAnalysis.slice(0, 2).map((item) => (
                  <span key={`llm-analysis-${item.label}`} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(236,72,153,0.1)', color: '#fbcfe8', fontFamily: 'monospace' }}>
                    {item.label}: {item.value.slice(0, 36)}{item.value.length > 36 ? '...' : ''}
                  </span>
                ))}
              </div>
            )}
            {step.data.rawResponse && (
              <details
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  fontFamily: 'monospace',
                  color: 'rgba(255,255,255,0.42)',
                  background: 'rgba(236,72,153,0.05)',
                  borderLeft: '2px solid #ec4899',
                  padding: '4px 8px',
                  borderRadius: '0 4px 4px 0',
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
                  {step.data.rawResponse.slice(0, 80)}
                  {step.data.rawResponse.length > 80 ? '…' : ''}
                </summary>
                <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#fbcfe8' }}>
                  {step.data.rawResponse}
                </pre>
              </details>
            )}
          </div>
        )}

        <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.32)' }}>file: {relatedFiles[0]}</div>
      </div>
    </div>
  )
}
